import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Briefcase, CalendarDays, ArrowLeft, Check, X, Send, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApi } from "@/hooks/useApi";
import { PageHeader, LoadingGrid, ErrorState, EmptyState, StatusBadge, Pager, Confirm, UploadButton, Field } from "@/components/common";
import { api, errMsg, fileUrl } from "@/lib/api";
import { inr, fdate, ftime } from "@/lib/format";
export function CreatorDeals() {
  const [tab, setTab] = useState("");
  const [page, setPage] = useState(1);
  const { data, loading, error, reload } = useApi("/me/deals", { status: tab, page });
  return (
    <div>
      <PageHeader eyebrow="Collaborations" title="Brand deals" desc="Campaigns assigned to you by the team." />
      <Tabs value={tab} onValueChange={(v) => { setTab(v); setPage(1); }} className="mb-6">
        <TabsList className="bg-card border border-border rounded-full p-1 h-auto max-w-full overflow-x-auto justify-start" style={{ scrollbarWidth: "none" }}>
          {[["", "All"], ["NEW", "New"], ["active", "Active"], ["completed", "Completed"], ["CANCELLED", "Cancelled"]].map(([v, l]) => (
            <TabsTrigger key={l} value={v} className="rounded-full px-3 sm:px-4 text-xs sm:text-sm whitespace-nowrap data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid={`deals-tab-${l.toLowerCase()}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
        {loading ? <LoadingGrid count={4} className="space-y-3" h="h-24" /> : error ? <ErrorState message={error} onRetry={reload} /> :
        data.items.length === 0 ? <EmptyState icon={Briefcase} title="No brand collaborations yet." desc="New opportunities will appear here." testid="deals-empty" /> : (
          <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 stagger">
            {data.items.map((d) => (
              <Link key={d.id} to={`/deals/${d.id}`} className="uc-card uc-card-hover overflow-hidden block" data-testid={`deal-card-${d.id}`}>
                <div className="relative aspect-[21/9] bg-secondary">
                  {d.campaign_banner ? <img src={fileUrl(d.campaign_banner)} alt="" className="absolute inset-0 h-full w-full object-cover" data-testid={`deal-banner-${d.id}`} /> : <div className="absolute inset-0 gold-bg opacity-[0.07]" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4 flex items-end gap-3">
                    {d.brand_logo && <img src={fileUrl(d.brand_logo)} alt="" className="h-11 w-11 rounded-xl object-cover border border-border bg-card" data-testid={`deal-logo-${d.id}`} />}
                    <div className="min-w-0 flex-1">
                      <p className="eyebrow">{d.brand_name}</p>
                      <p className="font-display text-lg leading-tight truncate">{d.campaign_name}</p>
                    </div>
                    <StatusBadge status={d.campaign_status} />
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 pt-3">
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{fdate(d.deadline)}</span>
                  <span className="num text-lg gold-text">{inr(d.creator_payout)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      {data && <Pager page={page} total={data.total} onPage={setPage} />}
    </div>
  );
}

function SubmitForm({ deal, onDone }) {
  const [f, setF] = useState({ text: "", link: "", file_url: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/me/deals/${deal.id}/submit`, f);
      toast.success("Content submitted");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="uc-card p-5 space-y-4" data-testid="deal-submit-form">
      <p className="font-display text-lg">Submit content</p>
      <Field label="Content link (reel / post URL)"><Input value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} className="rounded-xl bg-background" data-testid="submit-link-input" /></Field>
      <Field label="Notes / information"><Textarea value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} className="rounded-xl bg-background" data-testid="submit-text-input" /></Field>
      <div className="flex items-center gap-3 flex-wrap">
        <UploadButton accept="image/*,video/*,application/pdf" label={f.file_url ? "Replace file" : "Attach file"} onUploaded={(r) => setF({ ...f, file_url: r.url })} testid="submit-file-upload" />
        {f.file_url && <span className="text-xs text-primary">File attached</span>}
      </div>
      <Button onClick={submit} disabled={busy} className="rounded-full" data-testid="submit-content-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" />Submit</>}
      </Button>
    </div>
  );
}

export function CreatorDealDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: d, loading, error, reload } = useApi(`/me/deals/${id}`);
  const act = async (a) => {
    try {
      await api.post(`/me/deals/${id}/${a}`);
      toast.success(a === "accept" ? "Deal accepted" : "Deal declined");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (loading) return <LoadingGrid count={2} className="space-y-4" h="h-40" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="max-w-3xl">
      <button onClick={() => nav("/deals")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-6" data-testid="deal-back-button"><ArrowLeft className="h-4 w-4" />All deals</button>
      <div className="uc-card overflow-hidden fade-up" data-testid="deal-detail">
        {d.campaign_banner && (
          <div className="relative aspect-[21/9] bg-secondary" data-testid="deal-banner">
            <img src={fileUrl(d.campaign_banner)} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" />
          </div>
        )}
        <div className="p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            {d.brand_logo && <img src={fileUrl(d.brand_logo)} alt="" className="h-16 w-16 rounded-2xl object-cover border border-border shrink-0" data-testid="deal-logo" />}
            <div className="min-w-0">
              <p className="eyebrow" data-testid="deal-brand-name">{d.brand_name}</p>
              <h1 className="text-3xl font-semibold mt-1" data-testid="deal-campaign-name">{d.campaign_name}</h1>
            </div>
          </div>
          <StatusBadge status={d.campaign_status} testid="deal-status" />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-8">
          <div className="rounded-xl border border-border p-4"><p className="eyebrow">Agreed payout</p><p className="num text-2xl gold-text mt-2" data-testid="deal-payout">{inr(d.creator_payout)}</p></div>
          <div className="rounded-xl border border-border p-4"><p className="eyebrow">Deadline</p><p className="text-lg mt-2" data-testid="deal-deadline">{fdate(d.deadline)}</p></div>
        </div>
        {d.campaign_description && <div className="mt-8"><p className="eyebrow mb-2">Campaign</p><p className="text-sm text-muted-foreground whitespace-pre-wrap">{d.campaign_description}</p></div>}
        {d.brand_photo && (
          <div className="mt-8" data-testid="deal-brand-photo">
            <p className="eyebrow mb-3">About the brand</p>
            <img src={fileUrl(d.brand_photo)} alt={d.brand_name} className="w-full max-h-80 object-cover rounded-xl border border-border" />
          </div>
        )}
        <div className="mt-8">
          <p className="eyebrow mb-3">Deliverables</p>
          {d.deliverables?.length ? (
            <ul className="space-y-2" data-testid="deal-deliverables">{d.deliverables.map((x, i) => <li key={i} className="flex gap-2 text-sm"><Check className="h-4 w-4 text-primary mt-0.5" />{x}</li>)}</ul>
          ) : <p className="text-sm text-muted-foreground">—</p>}
        </div>
        {d.requirements && <div className="mt-8"><p className="eyebrow mb-2">Requirements</p><p className="text-sm text-muted-foreground whitespace-pre-wrap" data-testid="deal-requirements">{d.requirements}</p></div>}
        {d.brand_contact && <div className="mt-8"><p className="eyebrow mb-2">Brand contact</p><p className="text-sm" data-testid="deal-brand-contact">{d.brand_contact}</p></div>}
        {d.submission && (
          <div className="mt-8 rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm space-y-1" data-testid="deal-submission">
            <p className="eyebrow mb-1">Your submission · {ftime(d.submission.submitted_at)}</p>
            {d.submission.link && <a href={d.submission.link} target="_blank" rel="noreferrer" className="text-primary underline break-all block">{d.submission.link}</a>}
            {d.submission.text && <p className="text-muted-foreground whitespace-pre-wrap">{d.submission.text}</p>}
            {d.submission.file_url && <a href={fileUrl(d.submission.file_url)} target="_blank" rel="noreferrer" className="text-primary flex items-center gap-1"><FileText className="h-4 w-4" />Attached file</a>}
          </div>
        )}
        {d.campaign_status === "NEW" && (
          <div className="flex gap-3 mt-8">
            <Button className="rounded-full flex-1 sm:flex-none" onClick={() => act("accept")} data-testid="deal-accept-button"><Check className="h-4 w-4 mr-2" />Accept deal</Button>
            <Confirm title="Decline this deal?" desc="This cannot be undone." destructive confirmLabel="Decline" onConfirm={() => act("decline")} testid="decline-confirm"
              trigger={<Button variant="outline" className="rounded-full" data-testid="deal-decline-button"><X className="h-4 w-4 mr-2" />Decline</Button>} />
          </div>
        )}
        </div>
      </div>
      {["ACCEPTED", "CONTENT_SUBMITTED"].includes(d.campaign_status) && <div className="mt-6"><SubmitForm deal={d} onDone={reload} /></div>}
    </div>
  );
}
